import {EventEmitter, Injectable} from '@angular/core';
import {Ga4EventPayload} from '../../core/models/ga4-event-payload';
import {fixNonBreakingSpaceInObject, removeUndefinedOrNullFromObject} from '../../core/utils/utils';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';

@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsEventsService {

  eventsHistory: GA4EventDebugData[] = [];
  debugData: GA4EventDebugData[] = [];
  newEventSubmitted$: EventEmitter<GA4EventDebugData> = new EventEmitter<GA4EventDebugData>();
  readonly GA_COOKIE_KEY = '_ga';
  private eventsConfigs: GA4EventConfig[];
  private readonly CONTAINER_ID = 'G-M4ZE8STNWH';
  private readonly EVENTS_STORAGE_KEY = 'GA4_EVENTS';

  get containerIdCookieKey(): string {
    return this.CONTAINER_ID.replace('G-', '_ga_');
  }

  constructor() { }

  init(eventsConfigs: GA4EventConfig[]): void {
    this.eventsConfigs = eventsConfigs;
    this.initDebugData();
  }

  send(eventKey: string, partialPayload?: Ga4EventPayload): void {
    const config = this.getConfig(eventKey);
    const payload = config.buildPayload ? config.buildPayload(partialPayload) : null;
    this.sendEvent(eventKey, payload);
  }

  resetDebugData(): void {
    this.debugData = [];
  }

  eventSent(eventKey: string, payload: Ga4EventPayload): boolean {
    return !!this.eventsHistory.find(data => {
      if (data.eventKey === eventKey) {
        return Object.keys(payload)
            .map(payloadPropertyName => data.payload[payloadPropertyName] === payload[payloadPropertyName])
            .every(value => value);
      }
      return false;
    });
  }

  // cookie value = GA1.2.505382690.1650871704, client id = 505382690.1650871704
  getClientId(cookieValue: string): string | null {
    if (isNotNullOrUndefined(cookieValue)) {
      const parts = cookieValue.split('.');
      return parts.length === 4 ? parts.slice(parts.length - 2).join('.') : null;
    }
    return null;
  }

  // cookie value = GS1.1.1676455305.2.1.1676455495.59.0.0, session id = 1676455305
  getSessionId(cookieValue: string): string | null {
    if (isNotNullOrUndefined(cookieValue)) {
      const parts = cookieValue.split('.');
      return parts.length >= 3 ? parts[2] : null;
    }
    return null;
  }

  private sendEvent(eventKey: string, payload: Ga4EventPayload): void {
    const fullPayload = this.processPayload(this.constructFullPayload(eventKey, payload));
    window['dataLayer'] = window['dataLayer'] || [];
    window['dataLayer'].push(fullPayload);
    this.sendDebugInfo(eventKey, fullPayload);
  }

  private processPayload(payload: Ga4EventPayload): Ga4EventPayload {
    const newPayload = removeUndefinedOrNullFromObject(payload);
    return fixNonBreakingSpaceInObject(newPayload);
  }

  private constructFullPayload(eventKey: string, payload: Ga4EventPayload): Ga4EventPayload {
    const eventName = this.getEventName(eventKey);
    if (eventName) {
      return {
        event: eventName,
        ...payload
      };
    }
    return payload;
  }

  private getEventName(eventKey: string): string {
    const config = this.getConfig(eventKey);
    return config ? config.eventName : null;
  }

  private getConfig(eventKey: string): GA4EventConfig {
    return this.eventsConfigs.find(eventConfig => eventConfig.key === eventKey);
  }

  private sendDebugInfo(eventKey: string, payload: any): void {
    const eventData = {
      eventKey: eventKey,
      payload: payload,
    };
    this.addToEventsHistory(eventData);
    this.debugData.push(eventData);
    this.newEventSubmitted$.emit(eventData)
  }

  private addToEventsHistory(eventData) {
    this.eventsHistory.push(eventData);
    sessionStorage.setItem(this.EVENTS_STORAGE_KEY, JSON.stringify(this.eventsHistory));
  }

  private initDebugData(): void {
    const debugData = sessionStorage.getItem(this.EVENTS_STORAGE_KEY);
    if (isNotNullOrUndefined(debugData) && !!debugData.length) {
      this.eventsHistory.push(...JSON.parse(debugData));
      this.debugData.push(...JSON.parse(debugData));
    }
  }
}

export interface GA4EventConfig {
  key: string; // not included in event payload - used only to identify events
  eventName?: string; // GA4 event name, sent in event payload in 'event' property
  buildPayload?: (partialPayload?: Ga4EventPayload) => Ga4EventPayload; // function returning event payload not including 'event' property
}

export interface GA4ProductConfig {
  productName: string;
  stepsMapping: GA4StepMapping[];
  paymentMethods: GA4PaymentMethodConfig[]; // allows to link product payment methods with GA4 payment events
}

export interface GA4PaymentMethodConfig {
  method: string; // application payment method
  eventName: string; // event's event property
  eventPaymentType: string; // used to get translation for payment_type event property
}

export interface GA4StepMapping {
  stepKey: string;
  eventStepName: string;
}

export interface GA4EventDebugData {
  eventKey: string;
  payload?: any;
}
